class ProductStickyButton extends HTMLElement {
  constructor() {
    super();
    this.stickyButton = this.querySelector('[data-sticky-button]');
    this.sectionId = this.dataset.section;
    this.productInfo = document.querySelector(
      `product-info[data-section="${this.sectionId}"]`
    );
    this.mainForm = this.productInfo?.querySelector(
      `product-form[data-section-id="${this.sectionId}"]`
    );
    this.mainSubmitButton = document.querySelector(
      `#ProductSubmitButton-${this.sectionId}`
    );
    this.stickySubmitButton = this.querySelector(
      `#ProductSubmitButtonSticky-${this.sectionId}`
    );
    this.stickyForm = this.querySelector('form');
    this.stickySizeSelect = this.querySelector('[data-sticky-size-select]');
    this.mainFormElement = this.mainForm?.querySelector('form');
    this.isSyncing = false;
    this.variantChangeUnsubscriber = undefined;

    if (!this.stickyButton || !this.mainForm) return;

    this.init();
  }

  init() {
    this.syncVariantId();
    this.syncButtonState();
    this.syncStickySizeFromMain();
    this.initStickySizeSelect();
    this.initVariantSelectsListener();

    if (typeof subscribe === 'function') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        event => {
          if (event?.data?.sectionId !== this.sectionId) return;
          this.syncVariantId();
          this.syncButtonState();
          this.syncStickySizeFromMain();
          this.updateStickySizeOptions();
        }
      );
    }

    if (this.stickyForm) {
      this.stickyForm.addEventListener(
        'submit',
        () => {
          this.syncVariantId();
        },
        { capture: true }
      );
    }

    if (this.mainSubmitButton) {
      const observer = new MutationObserver(() => {
        this.syncButtonState();
      });

      observer.observe(this.mainSubmitButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    this.handleScroll();
    window.addEventListener('scroll', () => this.handleScroll(), {
      passive: true,
    });
  }

  disconnectedCallback() {
    this.variantChangeUnsubscriber?.();
  }

  initStickySizeSelect() {
    if (!this.stickySizeSelect) return;

    this.stickySizeSelect.addEventListener('change', () => {
      if (this.isSyncing) return;
      this.syncMainSizeFromSticky();
    });
  }

  initVariantSelectsListener() {
    if (!this.productInfo) return;

    this.productInfo.addEventListener('change', event => {
      if (this.isSyncing) return;
      if (event.target.closest('variant-selects')) {
        this.syncStickySizeFromMain();
      }
    });
  }

  syncStickySizeFromMain() {
    if (!this.stickySizeSelect || !window.SizeSelection) return;

    const sizeControls = window.SizeSelection.getSizeControls(this.productInfo);
    if (!sizeControls) return;

    this.isSyncing = true;

    if (sizeControls.type === 'radio') {
      const checked = sizeControls.element.querySelector(
        'input[type="radio"]:checked'
      );
      this.stickySizeSelect.value = checked?.value || '';
    } else {
      this.stickySizeSelect.value = sizeControls.element.value;
    }

    this.isSyncing = false;
  }

  syncMainSizeFromSticky() {
    if (!this.stickySizeSelect || !this.productInfo) return;

    const selectedValue = this.stickySizeSelect.value;
    const sizeControls = window.SizeSelection?.getSizeControls(this.productInfo);
    if (!sizeControls) return;

    this.isSyncing = true;

    if (!selectedValue) {
      window.SizeSelection?.resetSizeSelection(this.productInfo);
      window.SizeSelection?.updateBuyButtons(this.productInfo);
      this.isSyncing = false;
      return;
    }

    if (sizeControls.type === 'radio') {
      const radio = Array.from(
        sizeControls.element.querySelectorAll('input[type="radio"]')
      ).find(input => input.value === selectedValue);

      if (radio && !radio.disabled && !radio.classList.contains('disabled')) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      sizeControls.element.value = selectedValue;
      sizeControls.element.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.isSyncing = false;
  }

  updateStickySizeOptions() {
    if (!this.stickySizeSelect) return;

    const sizeControls = window.SizeSelection?.getSizeControls(this.productInfo);
    if (!sizeControls) return;

    if (sizeControls.type === 'radio') {
      const radios = sizeControls.element.querySelectorAll(
        'input[type="radio"]'
      );

      Array.from(this.stickySizeSelect.options).forEach(option => {
        if (!option.value) return;

        const radio = Array.from(radios).find(radio => radio.value === option.value);
        if (!radio) return;

        option.disabled =
          radio.disabled || radio.classList.contains('disabled');
      });
      return;
    }

    const mainOptions = sizeControls.element.options;
    Array.from(this.stickySizeSelect.options).forEach(option => {
      if (!option.value) return;

      const mainOption = Array.from(mainOptions).find(
        mainOption => mainOption.value === option.value
      );
      if (!mainOption) return;

      option.disabled = mainOption.disabled;
      option.textContent = mainOption.textContent;
    });
  }

  syncVariantId() {
    const mainVariantInput =
      this.mainFormElement?.querySelector('.product-variant-id');
    const stickyVariantInput =
      this.stickyForm?.querySelector('.product-variant-id');

    if (mainVariantInput && stickyVariantInput) {
      stickyVariantInput.value = mainVariantInput.value;
      stickyVariantInput.disabled = mainVariantInput.disabled;
    }
  }

  syncButtonState() {
    if (!this.mainSubmitButton || !this.stickySubmitButton) return;

    const isDisabled =
      this.mainSubmitButton.disabled ||
      this.mainSubmitButton.getAttribute('aria-disabled') === 'true';
    this.stickySubmitButton.disabled = isDisabled;

    const mainButtonText = this.mainSubmitButton
      .querySelector('span')
      ?.textContent.trim();
    if (mainButtonText) {
      const stickyButtonText =
        this.stickySubmitButton.querySelector('span');
      if (stickyButtonText) {
        stickyButtonText.textContent = mainButtonText;
      }
    }
  }

  handleScroll() {
    if (window.innerWidth >= 768) return;

    const scrollPosition = window.scrollY || window.pageYOffset;
    const shouldShow = scrollPosition > 200;

    if (shouldShow) {
      this.stickyButton.classList.remove('is-hidden');
    } else {
      this.stickyButton.classList.add('is-hidden');
    }
  }
}

customElements.define('product-sticky-button', ProductStickyButton);
